import { QrCodeIcon, HandThumbUpIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { Separator } from "@/core/components/ui/separator/separator";
import { Stack } from "@/core/components/ui/stack/stack";
import { Typography } from "@/core/components/ui/typography/typography";
import { BUSINESS_SETTINGS_ROUTES } from "@/business-settings/constants";
import { useBusiness } from "@/core/business";
import { cn } from "@/core/utils/utils";

interface CounterItemProps {
  className?: string;
  icon: React.ReactNode;
  title: string;
  value: number;
  onClick?: () => void;
}

const CounterItem = ({
  icon,
  title,
  value,
  className,
  onClick,
}: CounterItemProps) => (
  <Stack
    align="center"
    justify="center"
    className={className}
    onClick={onClick}
    role={onClick ? "button" : undefined}
  >
    {icon}
    <Stack gap="none" align="center">
      <Typography variant="subtitleW1" textColor="tertiary">
        {title}
      </Typography>
      <Typography variant="statNumberW2">{value}</Typography>
    </Stack>
  </Stack>
);

interface AdvocateAndReferralCountersProps {
  className?: string;
  direction?: "row" | "column";
  advocates?: number;
  referrals?: number;
}

export const AdvocateAndReferralCounters = ({
  direction = "row",
  className,
  advocates = 0,
  referrals = 0,
}: AdvocateAndReferralCountersProps) => {
  const navigate = useNavigate();
  const { businessId } = useBusiness();

  if (!businessId) return null; // preloaded at app level

  return (
    <Stack
      direction={direction}
      className={className}
      align="center"
      gap="lg"
      fullWidth
    >
      <CounterItem
        icon={<QrCodeIcon className="h-10 w-10" />}
        title="Advocates"
        value={advocates}
        className="flex-1"
        onClick={() =>
          navigate(
            `/business-settings/${businessId}${BUSINESS_SETTINGS_ROUTES.advocates}`,
          )
        }
      />
      <Separator
        orientation={direction === "row" ? "vertical" : "horizontal"}
        className={cn(direction === "row" && "h-[138px]")}
      />
      <CounterItem
        icon={<HandThumbUpIcon className="h-10 w-10" />}
        title="Referrals"
        value={referrals}
        className="flex-1"
        onClick={() => {
          navigate(
            `/business-settings/${businessId}${BUSINESS_SETTINGS_ROUTES.referralHistory}`,
          );
        }}
      />
    </Stack>
  );
};
